import ArticleIcon from "@mui/icons-material/Article";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import TableChartIcon from "@mui/icons-material/TableChart";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import axios from "axios";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import GlobalButton from "../../../components/CustomButton";
import { baseUrl } from "../../../services";
import { handleOpenSnackBar } from "../../../utils";

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const ALLOWED_EXTENSIONS = [
    "png",
    "jpg",
    "jpeg",
    "webp",
    "gif",
    "pdf",
    "doc",
    "docx",
    "xls",
    "xlsx",
    "ppt",
    "pptx",
    "txt",
    "csv",
    "rtf",
];

const ALLOWED_MIME_TYPES = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/webp",
    "image/gif",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "text/plain",
    "text/csv",
    "application/rtf",
    "text/rtf",
];

const ACCEPT_ATTRIBUTE = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(",");

const formatFileSize = (bytes = 0) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileExtension = (fileName = "") =>
    fileName.split(".").pop()?.toLowerCase() || "";

const isAllowedFile = (file) => {
    const extension = getFileExtension(file.name);
    const mimeType = file.type?.toLowerCase();

    if (!ALLOWED_EXTENSIONS.includes(extension)) {
        return false;
    }

    if (!mimeType) {
        return true;
    }

    return ALLOWED_MIME_TYPES.includes(mimeType);
};

const getAttachmentName = (file = {}) => file.originalname || file.name || "";

const MIME_TO_EXTENSION = {
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/webp": "webp",
    "image/gif": "gif",
    "application/pdf": "pdf",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/vnd.ms-powerpoint": "ppt",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
    "text/plain": "txt",
    "text/csv": "csv",
    "application/rtf": "rtf",
    "text/rtf": "rtf",
};

const getAttachmentExtension = (file = {}) => {
    if (file.extension) {
        return String(file.extension).replace(/^\./, "").toLowerCase();
    }

    const fromName = getFileExtension(getAttachmentName(file));
    if (fromName) return fromName;

    const mimeType = (file.mimeType || file.type || "").toLowerCase();
    if (MIME_TO_EXTENSION[mimeType]) {
        return MIME_TO_EXTENSION[mimeType];
    }

    if (mimeType.startsWith("image/")) {
        return mimeType.split("/")[1] || "";
    }

    return "";
};

const getFileIcon = (file = {}) => {
    const extension = getAttachmentExtension(file);

    if (["png", "jpg", "jpeg", "webp", "gif"].includes(extension)) {
        return <ImageIcon color="primary" />;
    }

    if (extension === "pdf") {
        return <PictureAsPdfIcon color="error" />;
    }

    if (["xls", "xlsx", "csv"].includes(extension)) {
        return <TableChartIcon color="success" />;
    }

    if (["doc", "docx", "rtf"].includes(extension)) {
        return <ArticleIcon color="info" />;
    }

    if (["ppt", "pptx"].includes(extension)) {
        return <SlideshowIcon color="warning" />;
    }

    if (extension === "txt") {
        return <TextSnippetIcon color="action" />;
    }

    return <AttachFileIcon color="action" />;
};

const Attachments = ({ attachments = [], setAttachments }) => {
    const fileInputRef = useRef(null);
    const { t } = useTranslation(["blog", "common"]);
    const [isUploading, setIsUploading] = useState(false);

    const uploadFile = async (file) => {
        const formData = new FormData();
        formData.append("image", file);

        const response = await axios.post(`${baseUrl}image/upload`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });

        const data = response?.data;
        if (!data?.url) {
            throw new Error(data?.message || "Upload failed");
        }

        return {
            url: data.url,
            originalname: data.originalname,
            extension: data.extension,
            mimeType: data.mimeType,
            size: data.size,
        };
    };

    const handleFileSelect = async (event) => {
        const files = Array.from(event.target.files || []);
        if (!files.length) return;

        const invalidFiles = files.filter((file) => !isAllowedFile(file));
        if (invalidFiles.length) {
            handleOpenSnackBar("error", t("validation.invalidAttachmentFormat"));
            event.target.value = "";
            return;
        }

        const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);
        if (oversizedFiles.length) {
            handleOpenSnackBar("error", t("validation.attachmentSizeLimit"));
            event.target.value = "";
            return;
        }

        try {
            setIsUploading(true);

            const uploadedFiles = await Promise.all(files.map((file) => uploadFile(file)));

            setAttachments((prev) => [...prev, ...uploadedFiles]);
            handleOpenSnackBar(
                "success",
                uploadedFiles.length > 1
                    ? t("multipleFilesUploadedSuccess", { count: uploadedFiles.length })
                    : t("fileAttachedSuccess", { ns: "common" })
            );
        } catch (error) {
            handleOpenSnackBar(
                "error",
                error?.message || t("fileUploadFailed", { ns: "common" })
            );
        } finally {
            setIsUploading(false);
            event.target.value = "";
        }
    };

    const handleRemoveAttachment = (index) => {
        setAttachments((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    };

    return (
        <Paper
            elevation={0}
            sx={{
                mt: 3,
                p: 3,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
            }}
        >
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={2}
            >
                <Box>
                    <Typography variant="h6" fontWeight={600}>
                        {t("blogAttachments")}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        {t("optional", { ns: "common" })}
                    </Typography>

                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                        {t("allowedAttachmentFormats")}
                    </Typography>
                </Box>

                <GlobalButton
                    btnText={isUploading ? "loading" : "uploadAttachment"}
                    startIcon={
                        isUploading ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : (
                            <AttachFileIcon sx={{ fontSize: 18 }} />
                        )
                    }
                    handleClickBtn={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    styleProps={{
                        width: "auto",
                        minWidth: 180,
                    }}
                />
            </Stack>

            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPT_ATTRIBUTE}
                onChange={handleFileSelect}
                hidden
            />

            {attachments.length > 0 ? (
                <Box mt={3}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={2}
                    >
                        <Typography variant="body2" color="text.secondary">
                            {t("attachmentsCount", { count: attachments.length })}
                        </Typography>

                        <Chip
                            color="primary"
                            size="small"
                            label={t("attachmentsCount", { count: attachments.length })}
                        />
                    </Stack>

                    <Stack spacing={1.5}>
                        {attachments.map((file, index) => (
                            <Box
                                key={`${file.url}-${index}`}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    bgcolor: "background.default",
                                }}
                            >
                                {getFileIcon(file)}

                                <Box flex={1} minWidth={0}>
                                    <Typography
                                        variant="body1"
                                        fontWeight={500}
                                        noWrap
                                        component="a"
                                        href={file.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ color: "primary.main", textDecoration: "none" }}
                                    >
                                        {getAttachmentName(file)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {formatFileSize(file.size)}
                                    </Typography>
                                </Box>

                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveAttachment(index)}
                                    aria-label="remove attachment"
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            ) : (
                <Box
                    mt={3}
                    onClick={() => !isUploading && fileInputRef.current?.click()}
                    sx={{
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: 2,
                        py: 4,
                        px: 2,
                        textAlign: "center",
                        bgcolor: "background.default",
                        cursor: isUploading ? "not-allowed" : "pointer",
                    }}
                >
                    <AttachFileIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                        {t("noAttachmentsAdded")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                        {t("clickToUploadAttachments")}
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default Attachments;
